import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDailyRewardsStatusEndpointDailyRewardsStatusGet } from "../client";
import { useOnboardingState } from "./useOnboardingState";

const SESSION_KEY = "dailyRewardsModalShown";

/**
 * Авто-відкриття щоденних нагород, якщо бекенд каже can_claim.
 * sessionStorage ставиться лише при закритті модалки — щоб не «втрачати» показ,
 * коли гравець пішов на інший екран, не взаємодіявши з модалкою.
 */
export function useDailyRewardsAutoOpen() {
  const [isOpen, setIsOpen] = useState(false);
  const { hasOnboardingCompleted, isLoading: isOnboardingLoading } = useOnboardingState();
  const onboardingDone = hasOnboardingCompleted();

  const { data: dailyRewardsStatus } = useQuery({
    queryKey: ["dailyRewardsStatus"],
    queryFn: async () => {
      const { data } = await getDailyRewardsStatusEndpointDailyRewardsStatusGet({ throwOnError: true });
      return data;
    },
    retry: 3,
  });

  // A player still onboarding gets no daily-rewards popup for the rest of this
  // app session: it covered the tutorial, and onboarding completing mid-session
  // would otherwise pop it over the tutorial-reward screens. They see it on
  // their next visit.
  useEffect(() => {
    if (!isOnboardingLoading && !onboardingDone) {
      sessionStorage.setItem(SESSION_KEY, "true");
    }
  }, [isOnboardingLoading, onboardingDone]);

  // (b) Open once when claimable status arrives — adjust during render (session gate + isOpen prevent loops)
  if (
    onboardingDone &&
    !isOpen &&
    dailyRewardsStatus?.days &&
    sessionStorage.getItem(SESSION_KEY) !== "true"
  ) {
    const hasClaimable = dailyRewardsStatus.days.some((d) => d.can_claim);
    if (hasClaimable) setIsOpen(true);
  }

  const onClose = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setIsOpen(false);
  }, []);

  return { isOpen, setIsOpen, onClose };
}
